import React, { useEffect, useState, useContext } from "react";

// Hooks
import useFetch from "hooks/useHTTP";
import useDrawer from "hooks/useDrawer";
import { useForm, FormProvider } from "react-hook-form";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

// Contexts
import { AppContext } from "context/AppContext";

// // Material UI
import { makeStyles } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";

// Config
import { tokenConfig } from "components/Table/config";

// Assets
import { ReactComponent as DeleteIcon } from "assets/delete-outlined.svg";
import plusIcon from "assets/plus-white.png";

// Components
import Button from "components/Button";
import Table from "components/Table";
import Layout from "components/Layout";
import Drawer from "components/Drawer";
import Loading from "components/Loading";
import TokenForm from "./components/TokenForm";
import Navigation from "../components/Navigation";

// Constants
import { PERSONAL_ACCESS_TOKEN } from "constants/api";
import { Box } from "@mui/material";
import { useCopyToClipboard } from "hooks/use-copy-to-clipboard";

// Styles
const useStyles = makeStyles(theme => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(4),
  },
  buttonIcon: {
    width: 16,
    height: 16,
  },
  loading: {
    height: "40vh",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
  },
  modalBody: {
    color: "black",
    whiteSpace: "pre-wrap",
  },
}));

const SettingsTokens = () => {
  const classes = useStyles({});
  const { copy } = useCopyToClipboard();
  const { get, post, del } = useFetch();
  const { setModalConfig, setGlobalLoading } = useContext(AppContext);
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [loadingDrawer, setLoadingDrawer] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const tokenForm = useForm({ defaultValues: { name: "" } });
  const { getValues } = useForm({
    defaultValues: { searchText: "" },
  });

  //Drawers state
  const {
    isDrawerOpen: isDrawerOpen,
    setIsDrawerOpen: setDrawerOpen,
    toggleDrawer: toggleDrawer,
  } = useDrawer();

  const fetchTokens = async () => {
    try {
      setLoadingTokens(true);
      const searchText = getValues("searchText");
      const qs = searchText ? `?q=${searchText}` : "";
      const res = await get(PERSONAL_ACCESS_TOKEN + qs);
      setLoadingTokens(false);
      if (!res.ok) return;
      setTokens(
        res.data.map(d => ({
          ...d,
          revoked: d.revoked.toString(),
          tokenPrefix: d.tokenPrefix + ".************************",
        }))
      );
    } catch (err) {
      setErrorMessage("Elaborazione non riuscita");
    } finally {
      setLoadingTokens(false);
    }
  };

  const deleteToken = async token => {
    try {
      setGlobalLoading(true);
      setModalConfig(null);
      const prefix = token.tokenPrefix.split(".")[0];
      const res = await del(`${PERSONAL_ACCESS_TOKEN}/${prefix}`);
      if (!res.ok) return;
      await fetchTokens();
      setGlobalLoading(false);
    } catch (err) {
      console.error(err);
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleTokenDelete = item => {
    setModalConfig({
      title: "Eliminazione Token",
      content: "Sei sicuro di voler eliminare questo token?",
      primaryAction: {
        text: "OK",
        onClick: () => deleteToken(item),
      },
      secondaryAction: {
        text: "ANNULLA",
      },
    });
  };

  const handleClosing = () => {
    setErrorMessage("");
    toggleDrawer();
    tokenForm.reset();
    setLoadingDrawer(false);
  };

  const onSubmit = async data => {
    try {
      setLoadingDrawer(true);
      const res = await post(PERSONAL_ACCESS_TOKEN, data);
      if (!res.ok) return;
      const tokenData = res.data;
      handleClosing();
      setModalConfig({
        title: "Token creato",
        content: (
          <>
            <Typography variant="h6" className={classes.modalBody}>
              {`Il token è stato creato correttamente.\n Salvalo ora, non potrai recuperarlo in futuro.`}
            </Typography>
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: theme => theme.palette.background.default,
              }}
            >
              <Button onClick={() => copy(tokenData.token)}>
                <ContentCopyRoundedIcon />
                Copia
              </Button>

              <Box mt={1}>{tokenData.token}</Box>
            </Box>
          </>
        ),
        primaryAction: {
          text: "OK",
          onClick: () => setModalConfig(null),
        },
      });
      fetchTokens();
    } catch (err) {
      console.error(err);
      setErrorMessage("Elaborazione non riuscita");
    } finally {
      setLoadingDrawer(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  return (
    <Layout showSecondCol={false} showHeader={false} showPosts={false}>
      <Navigation />
      <div className={classes.header}>
        <Typography variant="h5">Personal Access Tokens</Typography>
        <Button
          color="primary"
          variant="contained"
          startIcon={<img src={plusIcon} className={classes.buttonIcon} />}
          onClick={() => setDrawerOpen(true)}
        >
          Nuovo Token
        </Button>
      </div>

      {loadingTokens ? (
        <div className={classes.loading}>
          <Loading showWrapper={false} />
        </div>
      ) : (
        <>
          <Table
            data={tokens}
            config={tokenConfig}
            rowActions={[
              {
                onClick: item => handleTokenDelete(item),
                icon: <DeleteIcon />,
              },
            ]}
          />
        </>
      )}
      <FormProvider {...tokenForm}>
        <Drawer
          open={isDrawerOpen}
          title={!loadingDrawer && "Nuovo Token"}
          primaryText={!loadingDrawer && "SALVA"}
          secondaryText={!loadingDrawer && "ANNULLA"}
          onPrimary={tokenForm.handleSubmit(onSubmit)}
          onSecondary={() => handleClosing()}
          errorMessage={errorMessage}
        >
          <TokenForm loading={loadingDrawer} />
        </Drawer>
      </FormProvider>
    </Layout>
  );
};

export default SettingsTokens;
